import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '../../core/services/auth.service';
import { ApiService } from '../../services/api.service';

interface SelectOption { id: string; label: string; }

@Component({
  selector: 'app-employee-form',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatIconModule,
    MatTabsModule, MatProgressSpinnerModule
  ],
  templateUrl: './employee-form.component.html',
  styleUrl: './employee-form.component.scss'
})
export class EmployeeFormComponent implements OnInit, OnDestroy {
  employeeForm: FormGroup;
  isEditMode = false;
  loading = false;
  loadingOptions = true;
  saving = false;
  photoError: string | null = null;
  canViewSalary = false;
  canEditSalary = false;

  departments: SelectOption[] = [];
  teams: SelectOption[] = [];
  managers: SelectOption[] = [];
  designations: SelectOption[] = [];
  loginRoles: SelectOption[] = [
    { id: 'employee', label: 'Employee' },
    { id: 'developer', label: 'Developer' },
    { id: 'qa', label: 'QA' },
    { id: 'designer', label: 'Designer' },
    { id: 'aso', label: 'ASO' },
    { id: 'team_lead', label: 'Team lead' }
  ];

  genders = [
    { value: 0, label: 'Not specified' },
    { value: 1, label: 'Male' },
    { value: 2, label: 'Female' },
    { value: 3, label: 'Other' }
  ];
  maritalStatuses = [
    { value: 0, label: 'Not specified' },
    { value: 1, label: 'Single' },
    { value: 2, label: 'Married' },
    { value: 3, label: 'Divorced' },
    { value: 4, label: 'Widowed' },
    { value: 5, label: 'Separated' }
  ];
  bloodGroups = [
    { value: 0, label: 'Not specified' },
    { value: 1, label: 'A+' }, { value: 2, label: 'A-' },
    { value: 3, label: 'B+' }, { value: 4, label: 'B-' },
    { value: 5, label: 'AB+' }, { value: 6, label: 'AB-' },
    { value: 7, label: 'O+' }, { value: 8, label: 'O-' }
  ];
  employmentTypes = [
    { value: 0, label: 'Full time' },
    { value: 1, label: 'Part time' },
    { value: 2, label: 'Contract' },
    { value: 3, label: 'Intern' },
    { value: 4, label: 'Temporary' }
  ];
  statuses = [
    { value: 0, label: 'Active' },
    { value: 1, label: 'On leave' },
    { value: 2, label: 'Probation' },
    { value: 3, label: 'Terminated' },
    { value: 4, label: 'Resigned' }
  ];
  salaryTypes = [
    { value: 0, label: 'Not specified' },
    { value: 1, label: 'Monthly' },
    { value: 2, label: 'Hourly' },
    { value: 3, label: 'Daily' },
    { value: 4, label: 'Contract' }
  ];

  previewUrl: string | null = null;
  private sourceImage: HTMLImageElement | null = null;
  private pendingCroppedBlob: Blob | null = null;
  private pendingDeletePhoto = false;
  crop = { x: 0, y: 0, size: 200 };
  showCropper = false;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private auth: AuthService,
    public dialogRef: MatDialogRef<EmployeeFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isEditMode = !!data?.id;
    this.canViewSalary = this.auth.hasAnyRole(['hr', 'finance', 'super_admin', 'tenant_admin']);
    this.canEditSalary = this.canViewSalary;

    this.employeeForm = this.fb.group({
      firstName: [data?.firstName || '', Validators.required],
      middleName: [data?.middleName || ''],
      lastName: [data?.lastName || '', Validators.required],
      gender: [0],
      dateOfBirth: [''],
      maritalStatus: [0],
      bloodGroup: [0],
      nationalId: [''],
      passportNumber: [''],
      nationality: [''],
      religion: [''],
      personalEmail: [''],
      companyEmail: [data?.email || '', [Validators.required, Validators.email]],
      mobileNumber: [data?.phone || ''],
      alternateContact: [''],
      emergencyContactName: [''],
      emergencyContactPhone: [''],
      emergencyContactRelation: [''],
      currentAddress: [''],
      permanentAddress: [''],
      city: [''],
      province: [''],
      country: [''],
      postalCode: [''],
      hireDate: [data?.hireDate || ''],
      confirmationDate: [''],
      departmentId: [''],
      teamId: [''],
      jobTitle: [data?.position && data.position !== 'N/A' ? data.position : '', Validators.required],
      managerId: [''],
      teamLeadId: [''],
      projectManagerId: [''],
      hrManagerId: [''],
      employmentType: [0],
      status: [0],
      shift: [''],
      officeLocation: [''],
      password: [''],
      loginRole: [data?.loginRole || 'employee'],
      salaryType: [0],
      hourlyRate: [''],
      monthlySalary: [data?.salary ?? ''],
      overtimeRate: [''],
      fuelAllowance: [''],
      medicalAllowance: [''],
      otherAllowances: [''],
      taxNumber: [''],
      bankAccountHolderName: [data?.bankAccountHolderName || ''],
      bankName: [data?.bankName || ''],
      accountNumber: [data?.accountNumber || ''],
      iban: [data?.iban || ''],
      bankBranch: [data?.bankBranch || '']
    });
  }

  ngOnInit(): void {
    this.loadFormOptions();
    if (this.isEditMode) {
      this.loadProfile(String(this.data.id));
    }
  }

  ngOnDestroy(): void {
    if (this.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  get employeeCode(): string {
    return this.data?.employeeNumber || (this.isEditMode ? '—' : 'Auto-generated');
  }

  private loadFormOptions(): void {
    this.loadingOptions = true;
    this.api.getEmployeeFormData().subscribe({
      next: (response) => {
        const payload = response?.data ?? response ?? {};
        this.departments = this.mapOptions(payload.departments ?? payload.Departments, 'name');
        this.teams = this.mapOptions(payload.teams ?? payload.Teams, 'name');
        this.managers = (payload.managers ?? payload.Managers ?? []).map((m: any) => ({
          id: String(m.id ?? m.Id),
          label: `${m.name ?? m.Name}${m.employeeNumber || m.EmployeeNumber ? ` (${m.employeeNumber ?? m.EmployeeNumber})` : ''}`
        }));
        const titles = payload.employmentTypes ? [] : [];
        // designations not always returned from v1 form-data — keep free text + common list
        this.designations = [
          'Software Engineer', 'Senior Developer', 'QA Engineer', 'UI/UX Designer',
          'Project Manager', 'Team Lead', 'HR Manager', 'Finance Analyst', 'Staff'
        ].map(t => ({ id: t, label: t }));
        if (this.employeeForm.value.jobTitle) {
          this.ensureOption(this.designations, this.employeeForm.value.jobTitle);
        }
        void titles;
        this.loadingOptions = false;
      },
      error: () => { this.loadingOptions = false; }
    });
  }

  private loadProfile(id: string): void {
    this.loading = true;
    this.api.getEmployeeById(id).subscribe({
      next: (response) => {
        const p = response?.data ?? response ?? {};
        this.canViewSalary = !!p.canViewSalary;
        this.canEditSalary = !!p.canEditSalary;
        const salary = p.salary ?? {};

        this.employeeForm.patchValue({
          firstName: p.firstName ?? '',
          middleName: p.middleName ?? '',
          lastName: p.lastName ?? '',
          gender: p.gender ?? 0,
          dateOfBirth: p.dateOfBirth ?? '',
          maritalStatus: p.maritalStatus ?? 0,
          bloodGroup: p.bloodGroup ?? 0,
          nationalId: p.nationalId ?? '',
          passportNumber: p.passportNumber ?? '',
          nationality: p.nationality ?? '',
          religion: p.religion ?? '',
          personalEmail: p.personalEmail ?? '',
          companyEmail: p.companyEmail ?? '',
          mobileNumber: p.mobileNumber ?? '',
          alternateContact: p.alternateContact ?? '',
          emergencyContactName: p.emergencyContactName ?? '',
          emergencyContactPhone: p.emergencyContactPhone ?? '',
          emergencyContactRelation: p.emergencyContactRelation ?? '',
          currentAddress: p.currentAddress ?? '',
          permanentAddress: p.permanentAddress ?? '',
          city: p.city ?? '',
          province: p.province ?? '',
          country: p.country ?? '',
          postalCode: p.postalCode ?? '',
          hireDate: p.hireDate ?? '',
          confirmationDate: p.confirmationDate ?? '',
          departmentId: p.departmentId ?? '',
          teamId: p.teamId ?? '',
          jobTitle: p.jobTitle ?? '',
          managerId: p.managerId ?? '',
          teamLeadId: p.teamLeadId ?? '',
          projectManagerId: p.projectManagerId ?? '',
          hrManagerId: p.hrManagerId ?? '',
          employmentType: p.employmentType ?? 0,
          status: p.status ?? 0,
          shift: p.shift ?? '',
          officeLocation: p.officeLocation ?? p.workLocation ?? '',
          salaryType: salary.salaryType ?? 0,
          hourlyRate: salary.hourlyRate ?? '',
          monthlySalary: salary.monthlySalary ?? '',
          overtimeRate: salary.overtimeRate ?? '',
          fuelAllowance: salary.fuelAllowance ?? '',
          medicalAllowance: salary.medicalAllowance ?? '',
          otherAllowances: salary.otherAllowances ?? '',
          taxNumber: salary.taxNumber ?? '',
          bankAccountHolderName: salary.bankAccountHolderName ?? '',
          bankName: salary.bankName ?? '',
          accountNumber: salary.accountNumber ?? '',
          iban: salary.iban ?? '',
          bankBranch: salary.bankBranch ?? ''
        });

        this.data = { ...this.data, employeeNumber: p.employeeNumber, id: p.id };
        if (p.hasProfilePicture) {
          this.api.getEmployeePhotoBlob(p.id).subscribe({
            next: (blob) => {
              this.previewUrl = URL.createObjectURL(blob);
            }
          });
        }
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  onFileSelected(event: Event): void {
    this.photoError = null;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      this.photoError = 'Only JPG, PNG, and WEBP images are allowed.';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.photoError = 'Maximum size is 5 MB.';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        this.sourceImage = img;
        const side = Math.min(img.width, img.height);
        this.crop = {
          x: Math.floor((img.width - side) / 2),
          y: Math.floor((img.height - side) / 2),
          size: side
        };
        this.showCropper = true;
        this.renderCropPreview();
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  applyCrop(): void {
    if (!this.sourceImage) return;
    const canvas = document.createElement('canvas');
    const out = 400;
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(
      this.sourceImage,
      this.crop.x, this.crop.y, this.crop.size, this.crop.size,
      0, 0, out, out
    );
    canvas.toBlob((blob) => {
      if (!blob) return;
      this.pendingCroppedBlob = blob;
      this.pendingDeletePhoto = false;
      if (this.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = URL.createObjectURL(blob);
      this.showCropper = false;
    }, 'image/jpeg', 0.92);
  }

  cancelCrop(): void {
    this.showCropper = false;
    this.sourceImage = null;
  }

  deletePhoto(): void {
    this.pendingCroppedBlob = null;
    this.pendingDeletePhoto = true;
    if (this.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
  }

  private renderCropPreview(): void {
    // Preview is applied after crop; cropper UI uses CSS overlay on source dimensions.
  }

  moveCrop(dx: number, dy: number): void {
    if (!this.sourceImage) return;
    this.crop.x = Math.max(0, Math.min(this.sourceImage.width - this.crop.size, this.crop.x + dx));
    this.crop.y = Math.max(0, Math.min(this.sourceImage.height - this.crop.size, this.crop.y + dy));
  }

  resizeCrop(delta: number): void {
    if (!this.sourceImage) return;
    const next = Math.max(80, Math.min(
      Math.min(this.sourceImage.width, this.sourceImage.height),
      this.crop.size + delta
    ));
    this.crop.size = next;
    this.crop.x = Math.max(0, Math.min(this.sourceImage.width - next, this.crop.x));
    this.crop.y = Math.max(0, Math.min(this.sourceImage.height - next, this.crop.y));
  }

  save(): void {
    if (this.employeeForm.invalid) {
      this.employeeForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    const v = this.employeeForm.getRawValue();

    if (this.isEditMode) {
      const profile = this.toProfilePayload(v);
      this.api.updateEmployeeProfile(this.data.id, profile).subscribe({
        next: () => this.afterSavePhotos(String(this.data.id)),
        error: (err) => {
          this.photoError = err?.error?.message || err?.message || 'Failed to save profile.';
          this.saving = false;
        }
      });
      return;
    }

    // Create via legacy form (user + employee + login), then enrich profile.
    const createPayload = {
      firstName: v.firstName,
      lastName: v.lastName,
      email: v.companyEmail,
      phone: v.mobileNumber,
      department: this.departments.find(d => d.id === v.departmentId)?.label || v.departmentId,
      position: v.jobTitle,
      salary: v.monthlySalary || 0,
      hireDate: v.hireDate,
      status: 'Active',
      password: v.password || undefined,
      loginRole: v.loginRole
    };

    this.api.createEmployee(createPayload).subscribe({
      next: (response) => {
        const payload = response?.data ?? response ?? {};
        const id = payload.id ?? payload.Id;
        const creds = payload.loginCredentials ?? payload.LoginCredentials;
        if (creds?.email || creds?.Email) {
          const email = creds.email ?? creds.Email;
          const password = creds.password ?? creds.Password;
          const role = creds.loginRole ?? creds.LoginRole ?? 'employee';
          window.alert(`Employee login created.\n\nEmail: ${email}\nPassword: ${password}\nRole: ${role}`);
        }
        if (id) {
          this.api.updateEmployeeProfile(id, this.toProfilePayload(v)).subscribe({
            next: () => this.afterSavePhotos(String(id)),
            error: () => {
              this.saving = false;
              this.dialogRef.close(true);
            }
          });
        } else {
          this.saving = false;
          this.dialogRef.close(true);
        }
      },
      error: (err) => {
        this.photoError = err?.error?.message || err?.message || 'Failed to create employee.';
        this.saving = false;
      }
    });
  }

  private afterSavePhotos(id: string): void {
    if (this.pendingDeletePhoto) {
      this.api.deleteEmployeePhoto(id).subscribe({
        next: () => this.finish(),
        error: () => this.finish()
      });
      return;
    }
    if (this.pendingCroppedBlob) {
      this.api.uploadEmployeePhoto(id, this.pendingCroppedBlob, 'profile.jpg').subscribe({
        next: () => this.finish(),
        error: (err) => {
          this.photoError = err?.error?.message || 'Profile saved but photo upload failed.';
          this.saving = false;
        }
      });
      return;
    }
    this.finish();
  }

  private finish(): void {
    this.saving = false;
    this.dialogRef.close(true);
  }

  private toProfilePayload(v: any): any {
    const payload: any = {
      firstName: v.firstName,
      middleName: v.middleName || null,
      lastName: v.lastName,
      gender: Number(v.gender) || 0,
      dateOfBirth: v.dateOfBirth || null,
      maritalStatus: Number(v.maritalStatus) || 0,
      bloodGroup: Number(v.bloodGroup) || 0,
      nationalId: v.nationalId || null,
      passportNumber: v.passportNumber || null,
      nationality: v.nationality || null,
      religion: v.religion || null,
      personalEmail: v.personalEmail || null,
      companyEmail: v.companyEmail || null,
      mobileNumber: v.mobileNumber || null,
      alternateContact: v.alternateContact || null,
      emergencyContactName: v.emergencyContactName || null,
      emergencyContactPhone: v.emergencyContactPhone || null,
      emergencyContactRelation: v.emergencyContactRelation || null,
      currentAddress: v.currentAddress || null,
      permanentAddress: v.permanentAddress || null,
      city: v.city || null,
      province: v.province || null,
      country: v.country || null,
      postalCode: v.postalCode || null,
      hireDate: v.hireDate || null,
      confirmationDate: v.confirmationDate || null,
      departmentId: v.departmentId || null,
      teamId: v.teamId || null,
      jobTitle: v.jobTitle,
      managerId: v.managerId || null,
      teamLeadId: v.teamLeadId || null,
      projectManagerId: v.projectManagerId || null,
      hrManagerId: v.hrManagerId || null,
      employmentType: Number(v.employmentType) || 0,
      status: Number(v.status) || 0,
      shift: v.shift || null,
      officeLocation: v.officeLocation || null,
      workLocation: v.officeLocation || null
    };

    if (this.canEditSalary) {
      payload.salaryType = Number(v.salaryType) || 0;
      payload.hourlyRate = this.num(v.hourlyRate);
      payload.monthlySalary = this.num(v.monthlySalary);
      payload.overtimeRate = this.num(v.overtimeRate);
      payload.fuelAllowance = this.num(v.fuelAllowance);
      payload.medicalAllowance = this.num(v.medicalAllowance);
      payload.otherAllowances = this.num(v.otherAllowances);
      payload.taxNumber = v.taxNumber || null;
      payload.bankAccountHolderName = v.bankAccountHolderName || null;
      payload.bankName = v.bankName || null;
      payload.accountNumber = v.accountNumber || null;
      payload.iban = v.iban || null;
      payload.bankBranch = v.bankBranch || null;
    }

    return payload;
  }

  private num(v: any): number | null {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  private mapOptions(list: any, labelKey: string): SelectOption[] {
    return (Array.isArray(list) ? list : [])
      .map((d: any) => ({
        id: String(d.id ?? d.Id ?? ''),
        label: String(d[labelKey] ?? d.Name ?? d.name ?? d.label ?? '')
      }))
      .filter((d: SelectOption) => !!d.id && !!d.label);
  }

  private ensureOption(list: SelectOption[], value: string): void {
    if (value && !list.some(x => x.label === value || x.id === value)) {
      list.push({ id: value, label: value });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
